import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { ResetPasswordModal } from 'components/modals/modules/admin-module/ResetPasswordModal';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useUserManagement, useAdminResetPassword } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
import type { UserCacheRecord } from 'api/types/userManagement.types';

// ============================================================================
// ROLE LABEL MAPPING
// ============================================================================

const ROLE_LABELS: Record<string, string> = {
  BRANCH_USER: 'Branch User',
  APPROVER: 'Approver',
  ADMIN: 'Admin',
  SYSTEM: 'System',
};

// ============================================================================
// MAIN USER MANAGEMENT SCREEN
// ============================================================================

const UserManagementScreen: React.FC = () => {
  const { data: users, loading: apiLoading, error: apiError, refetch } = useUserManagement();
  const { resetPassword, loading: resetLoading } = useAdminResetPassword();

  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<string>('list');
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserCacheRecord | null>(null);

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return (users || []).filter((user) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        user.displayName?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        (ROLE_LABELS[user.role] || user.role || '').toLowerCase().includes(q)
      );
    });
  }, [users, searchQuery]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleResetPassword = useCallback((user: UserCacheRecord) => {
    setSelectedUser(user);
    setResetModalOpen(true);
  }, []);

  const handleResetSubmit = useCallback(async (data: { password: string; temporary: boolean }) => {
    if (!selectedUser) return;
    try {
      await resetPassword(selectedUser.userId, data);
      showMessage(`Password reset successfully for ${selectedUser.displayName}`, 'success');
      setResetModalOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message;
      throw new Error(apiMessage || 'Failed to reset password');
    }
  }, [selectedUser, resetPassword, showMessage]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: UserCacheRecord): ActionMenuItem[] => [
    {
      id: 'reset-password',
      label: 'Reset Password',
      icon: <IconSystem.lock size={18} />,
      onClick: () => handleResetPassword(row),
    },
  ], [handleResetPassword]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<UserCacheRecord>[] = useMemo(() => [
    { id: 'displayName', header: 'User Name', accessorKey: 'displayName', size: 200 },
    { id: 'username', header: 'Login ID', accessorKey: 'username', size: 160 },
    { id: 'email', header: 'Email', accessorKey: 'email', size: 250 },
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role',
      size: 130,
      cell: ({ getValue }: any) => {
        const role = getValue() as string;
        return ROLE_LABELS[role] || role || '—';
      },
    },
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      accessorKey: 'lastSeenAt',
      size: 150,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }: any) => {
        const isActive = getValue();
        return (
          <span style={getStatusBadgeStyle(isActive ? 'ACTIVE' : 'INACTIVE')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="User Management"
        icon={<IconSystem.users size={20} />}
        description="Manage user accounts and passwords"
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search users...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && (users || []).length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load users</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && (users || []).length === 0 ? (
          <GridLoadingSkeleton columns={5} />
        ) : (
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                rowActions={getRowActions}
                enablePagination
                enableColumnFilters={false}
                emptyMessage="No users found"
              />
            )}
          </div>
        )}
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={resetModalOpen}
        onClose={() => { setResetModalOpen(false); setSelectedUser(null); }}
        onSubmit={handleResetSubmit}
        userName={selectedUser?.displayName || ''}
        isLoading={resetLoading}
      />
    </div>
  );
};

export default UserManagementScreen;
